import { authFetch } from './auth/authFetch';

export const API_BASE = process.env.REACT_APP_API_URL || '';

export function apiFetch(input, init = {}) {
  return authFetch(input, init);
}

export class ApiError extends Error {
  constructor(message, { status, timeout = false } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.timeout = timeout;
  }
}

// POST JSON and return the parsed body, throwing a typed ApiError on failure.
// 503/504 (API Gateway integration timeout for long LLM calls) becomes a
// friendly, retryable timeout error.
export async function postJson(path, body) {
  const res = await apiFetch(`${API_BASE}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (res.status === 503 || res.status === 504) {
    throw new ApiError(
      'Die Anfrage hat zu lange gedauert (Zeitüberschreitung). Bitte versuche es erneut.',
      { status: res.status, timeout: true }
    );
  }

  let data;
  try {
    data = await res.json();
  } catch {
    throw new ApiError(`Unerwartete Antwort vom Server (Status ${res.status}).`, { status: res.status });
  }

  if (!res.ok || data?.error) {
    throw new ApiError(data?.error || `Serverfehler (Status ${res.status}).`, { status: res.status });
  }
  return data;
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Submit a long-running job (POST → 202 {jobId}) and poll its status endpoint
// until done/error. Returns the job result. Used for map-capabilities and
// implement, which exceed API Gateway's 30s synchronous limit.
export async function submitJob(submitPath, body, jobsPath, options = {}) {
  const { intervalMs = 2000, timeoutMs = 180_000, isCancelled = () => false } = options;

  const res = await apiFetch(`${API_BASE}${submitPath}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (res.status === 503 || res.status === 504) {
    throw new ApiError('Der Dienst ist gerade nicht verfügbar. Bitte versuche es erneut.', {
      status: res.status, timeout: true,
    });
  }
  let enq;
  try { enq = await res.json(); } catch {
    throw new ApiError(`Unerwartete Antwort vom Server (Status ${res.status}).`, { status: res.status });
  }
  if (!res.ok || !enq?.jobId) {
    throw new ApiError(enq?.error || `Auftrag konnte nicht gestartet werden (Status ${res.status}).`, { status: res.status });
  }

  const deadline = Date.now() + timeoutMs;
  while (Date.now() < deadline) {
    if (isCancelled()) return undefined;
    await sleep(intervalMs);
    if (isCancelled()) return undefined;

    let jr;
    try { jr = await apiFetch(`${API_BASE}${jobsPath}/${enq.jobId}`); } catch { continue; }
    if (!jr.ok) continue; // transient (e.g. during a deploy) — keep polling

    let job;
    try { job = await jr.json(); } catch { continue; }
    if (job.status === 'done') return job.result;
    if (job.status === 'error') {
      throw new ApiError(job.error || 'Die Verarbeitung ist fehlgeschlagen.', { status: 500 });
    }
  }
  throw new ApiError('Zeitüberschreitung beim Warten auf das Ergebnis. Bitte versuche es erneut.', { timeout: true });
}
